import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { movelAPI, usersAPI, formatarErroAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Smartphone, Loader2, Search, Download, RotateCcw, Pencil, Eye, ExternalLink, ChevronLeft, ChevronRight, AlertTriangle, BellRing } from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { LinhaMovelDialog, StatusLinhaBadge } from '@/components/movel/LinhaMovelDialog';

const POR_PAGINA = 20;
const PERFIS_EDITAM = ['ADMIN', 'BKO', 'BKO_MAILING', 'GERENTE'];

const periodoParaDatas = (periodo) => {
  const hoje = new Date();
  if (periodo === 'mes_atual') return { data_inicio: format(startOfMonth(hoje), 'yyyy-MM-dd'), data_fim: format(endOfMonth(hoje), 'yyyy-MM-dd') };
  if (periodo === 'mes_anterior') { const m = subMonths(hoje, 1); return { data_inicio: format(startOfMonth(m), 'yyyy-MM-dd'), data_fim: format(endOfMonth(m), 'yyyy-MM-dd') }; }
  if (periodo === 'tres_meses') return { data_inicio: format(startOfMonth(subMonths(hoje, 2)), 'yyyy-MM-dd'), data_fim: format(endOfMonth(hoje), 'yyyy-MM-dd') };
  return {};
};

const fmtDataCurta = (iso) => { try { return format(new Date(iso), 'dd/MM/yy'); } catch { return '-'; } };

const Movel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const podeEditar = PERFIS_EDITAM.includes(user?.perfil);
  const isVendedor = user?.perfil === 'VENDEDOR';

  const [dados, setDados] = useState({ linhas: [], total: 0, ativas: 0, nao_ativas: 0 });
  const [loading, setLoading] = useState(true);
  const [vendedores, setVendedores] = useState([]);
  const [filtros, setFiltros] = useState({ periodo: 'mes_atual', status_linha: 'todos', vendedor_id: 'todos', busca: '' });
  const [pagina, setPagina] = useState(1);
  const [selecionada, setSelecionada] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [pendentes, setPendentes] = useState({ linhas: [], total: 0, dias_limite: 3 });
  const [modoPendentes, setModoPendentes] = useState(false);
  const [notificando, setNotificando] = useState(false);

  const montarParams = useCallback(() => {
    const p = { ...periodoParaDatas(filtros.periodo) };
    if (filtros.status_linha !== 'todos') p.status_linha = filtros.status_linha;
    if (filtros.vendedor_id !== 'todos') p.vendedor_id = filtros.vendedor_id;
    if (filtros.busca.trim()) p.busca = filtros.busca.trim();
    return p;
  }, [filtros]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await movelAPI.listar(montarParams());
      setDados(res.data);
      setPagina(1);
    } catch (error) {
      toast.error(formatarErroAPI(error));
    } finally {
      setLoading(false);
    }
  }, [montarParams]);

  useEffect(() => { carregar(); }, [filtros.periodo, filtros.status_linha, filtros.vendedor_id]); // eslint-disable-line

  const carregarPendentes = useCallback(() => movelAPI.pendentes().then((r) => setPendentes(r.data)).catch(() => {}), []);
  useEffect(() => { carregarPendentes(); }, [carregarPendentes]);

  const notificarAgora = async () => {
    setNotificando(true);
    try {
      const r = await movelAPI.notificarPendentes();
      toast.success(r.data?.message || 'Alertas enviados');
    } catch (error) {
      toast.error(formatarErroAPI(error));
    } finally {
      setNotificando(false);
    }
  };

  useEffect(() => {
    if (isVendedor) return;
    usersAPI.listVendedores().then((r) => setVendedores((r.data || []).filter((u) => u.ativo !== false))).catch(() => {});
  }, [isVendedor]);

  const salvarLinha = async (form) => {
    if (!selecionada) return;
    setSalvando(true);
    try {
      const res = await movelAPI.atualizar(selecionada.id, form);
      toast.success(res.data?.message === 'Nenhuma alteração' ? 'Nenhuma alteração para salvar' : 'Linha atualizada com sucesso!');
      setSelecionada(null);
      carregar();
      carregarPendentes();
    } catch (error) {
      toast.error(formatarErroAPI(error), { duration: 6000 });
    } finally {
      setSalvando(false);
    }
  };

  const exportar = async () => {
    setExportando(true);
    try {
      const res = await movelAPI.exportar(montarParams());
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `movel_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Planilha exportada!');
    } catch (error) {
      toast.error('Erro ao exportar');
    } finally {
      setExportando(false);
    }
  };

  const limpar = () => setFiltros({ periodo: 'mes_atual', status_linha: 'todos', vendedor_id: 'todos', busca: '' });

  const linhasVisiveis = modoPendentes ? pendentes.linhas : dados.linhas;
  const totalPaginas = Math.max(1, Math.ceil(linhasVisiveis.length / POR_PAGINA));
  const linhasPagina = linhasVisiveis.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  return (
    <div className="space-y-6" data-testid="movel-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Smartphone className="h-7 w-7 text-purple-500" />
            Móvel
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Uma linha por chip, gerada automaticamente das vendas com produto móvel. {podeEditar ? 'BKO/Admin editam ICCID, telefone, código e status.' : 'Consulta somente leitura.'}
          </p>
        </div>
        <Button variant="outline" onClick={exportar} disabled={exportando || dados.total === 0} data-testid="btn-exportar-movel">
          {exportando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
          Exportar Excel
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="movel-stats">
        <Card><CardContent className="pt-4"><div className="text-2xl font-bold" data-testid="stat-total">{dados.total}</div><div className="text-sm text-gray-500">Linhas</div></CardContent></Card>
        <Card className="border-emerald-700/40"><CardContent className="pt-4"><div className="text-2xl font-bold text-emerald-500" data-testid="stat-ativas">{dados.ativas}</div><div className="text-sm text-emerald-500">Ativas</div></CardContent></Card>
        <Card className="border-red-700/40"><CardContent className="pt-4"><div className="text-2xl font-bold text-red-500" data-testid="stat-nao-ativas">{dados.nao_ativas}</div><div className="text-sm text-red-500">Não ativas</div></CardContent></Card>
        <Card
          className={`cursor-pointer transition-colors ${modoPendentes ? 'border-amber-500 bg-amber-500/10' : 'border-amber-700/40 hover:bg-amber-500/5'}`}
          onClick={() => { setModoPendentes(!modoPendentes); setPagina(1); }}
          data-testid="stat-chips-pendentes"
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold text-amber-500" data-testid="stat-pendentes-total">{pendentes.total}</div>
              <AlertTriangle className="h-5 w-5 text-amber-500" />
            </div>
            <div className="text-sm text-amber-500">Chips pendentes</div>
            <div className="text-[11px] text-gray-500">Instaladas há +{pendentes.dias_limite} dias, "Não ativo" ou sem ICCID</div>
          </CardContent>
        </Card>
      </div>

      {modoPendentes && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-2" data-testid="banner-pendentes">
          <p className="text-sm text-amber-300 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Mostrando só os chips pendentes ({pendentes.total}). Os envolvidos (vendedor, supervisor, BKO e admin) recebem alerta automático 1x por dia.</p>
          <div className="flex gap-2">
            {podeEditar && (
              <Button size="sm" variant="outline" onClick={notificarAgora} disabled={notificando || pendentes.total === 0} data-testid="btn-notificar-pendentes">
                {notificando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <BellRing className="mr-2 h-4 w-4" />}Alertar agora
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setModoPendentes(false)} data-testid="btn-sair-pendentes">Ver todas</Button>
          </div>
        </div>
      )}

      <Card>
        <CardContent className="py-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div>
              <Label className="text-xs">Período</Label>
              <Select value={filtros.periodo} onValueChange={(v) => setFiltros({ ...filtros, periodo: v })}>
                <SelectTrigger className="mt-1" data-testid="filtro-periodo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mes_atual">Mês atual</SelectItem>
                  <SelectItem value="mes_anterior">Mês anterior</SelectItem>
                  <SelectItem value="tres_meses">Últimos 3 meses</SelectItem>
                  <SelectItem value="todos">Todo o período</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status da linha</Label>
              <Select value={filtros.status_linha} onValueChange={(v) => setFiltros({ ...filtros, status_linha: v })}>
                <SelectTrigger className="mt-1" data-testid="filtro-status-linha"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="Ativo">🟢 Ativo</SelectItem>
                  <SelectItem value="Não ativo">🔴 Não ativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!isVendedor && (
              <div>
                <Label className="text-xs">Vendedor</Label>
                <Select value={filtros.vendedor_id} onValueChange={(v) => setFiltros({ ...filtros, vendedor_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="filtro-vendedor"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {vendedores.map((v) => <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className={isVendedor ? 'md:col-span-2' : ''}>
              <Label className="text-xs">Buscar (cliente, CPF/CNPJ, ICCID, telefone, protocolo)</Label>
              <Input className="mt-1" value={filtros.busca} onChange={(e) => setFiltros({ ...filtros, busca: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && carregar()} placeholder="Digite e pressione Enter" data-testid="filtro-busca" />
            </div>
            <div className="flex gap-2">
              <Button onClick={carregar} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-consultar-movel"><Search className="mr-2 h-4 w-4" />Consultar</Button>
              <Button variant="outline" onClick={limpar} data-testid="btn-limpar-movel"><RotateCcw className="h-4 w-4" /></Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{modoPendentes ? `Chips pendentes (${pendentes.total})` : `Linhas (${dados.total})`}</CardTitle>
          {totalPaginas > 1 && (
            <div className="flex items-center gap-2 text-sm">
              <Button size="sm" variant="outline" disabled={pagina === 1} onClick={() => setPagina(pagina - 1)} data-testid="btn-pagina-anterior"><ChevronLeft className="h-4 w-4" /></Button>
              <span data-testid="paginacao-info">{pagina} / {totalPaginas}</span>
              <Button size="sm" variant="outline" disabled={pagina === totalPaginas} onClick={() => setPagina(pagina + 1)} data-testid="btn-pagina-proxima"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto text-purple-500" /><p className="text-gray-500 mt-2">Carregando...</p></div>
          ) : linhasVisiveis.length === 0 ? (
            <div className="text-center py-12 rounded-lg border border-dashed" data-testid="movel-vazio">
              <Smartphone className="h-12 w-12 mx-auto text-gray-500 mb-3" />
              <p className="text-gray-500">{modoPendentes ? 'Nenhum chip pendente — tudo em dia!' : 'Nenhuma linha móvel encontrada no período'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="movel-table">
                <thead>
                  <tr className="border-b bg-purple-950/30 text-left text-xs uppercase tracking-wide">
                    <th className="p-2">Data</th><th className="p-2">Protocolo</th><th className="p-2">Cliente</th><th className="p-2">CPF/CNPJ</th>
                    <th className="p-2">Vendedor</th><th className="p-2">HP</th><th className="p-2">Plano</th><th className="p-2 text-center">Linha</th>
                    <th className="p-2">Tipo</th><th className="p-2">DDD</th><th className="p-2">Número</th><th className="p-2">Operadora</th><th className="p-2">ICCID</th>
                    <th className="p-2">Telefone</th><th className="p-2">Cód. Aut.</th><th className="p-2">Status</th><th className="p-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {linhasPagina.map((l) => (
                    <tr key={l.id} className="border-b border-gray-700/30 hover:bg-purple-950/20" data-testid={`linha-movel-${l.id}`}>
                      <td className="p-2 whitespace-nowrap">{fmtDataCurta(l.venda_criado_em)}</td>
                      <td className="p-2 font-mono font-semibold text-red-400">{l.protocolo}</td>
                      <td className="p-2 font-medium max-w-[180px] truncate" title={l.cliente_nome}>{l.cliente_nome}</td>
                      <td className="p-2 font-mono text-xs">{l.cliente_documento}</td>
                      <td className="p-2 max-w-[140px] truncate" title={l.vendedor_nome}>{l.vendedor_nome}</td>
                      <td className="p-2 font-mono text-xs">{l.hp || '-'}</td>
                      <td className="p-2 max-w-[150px] truncate" title={l.plano_movel_nome}>{l.plano_movel_nome}</td>
                      <td className="p-2 text-center"><Badge variant="outline">{l.indice}/{l.qtd_linhas}</Badge></td>
                      <td className="p-2">{l.tipo_linha}</td>
                      <td className="p-2">{l.ddd || '-'}</td>
                      <td className="p-2 whitespace-nowrap">{l.numero_linha || '-'}</td>
                      <td className="p-2">{l.operadora || '-'}</td>
                      <td className="p-2 font-mono text-xs">{l.iccid || '-'}</td>
                      <td className="p-2 whitespace-nowrap">{l.telefone || '-'}</td>
                      <td className="p-2 font-mono text-xs">{l.codigo_autorizacao || '-'}</td>
                      <td className="p-2">
                        <div className="flex flex-col gap-1 items-start">
                          <StatusLinhaBadge status={l.status_linha} />
                          {l.dias_pendente != null && (
                            <Badge className="bg-amber-500 text-black border-0 text-[10px]" title={l.motivo_pendencia || 'Pendente'} data-testid={`badge-pendente-${l.id}`}>
                              <AlertTriangle className="h-3 w-3 mr-1" /> há {l.dias_pendente} dias
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" title="Abrir venda" onClick={() => navigate(`/vendas/${l.venda_id}`)} data-testid={`btn-abrir-venda-${l.id}`}><ExternalLink className="h-4 w-4 text-gray-400" /></Button>
                        <Button size="sm" variant="ghost" title={podeEditar ? 'Editar linha' : 'Ver linha'} onClick={() => setSelecionada(l)} data-testid={`btn-editar-linha-${l.id}`}>
                          {podeEditar ? <Pencil className="h-4 w-4 text-purple-400" /> : <Eye className="h-4 w-4 text-purple-400" />}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <LinhaMovelDialog linha={selecionada} aberto={!!selecionada} onFechar={() => setSelecionada(null)} podeEditar={podeEditar} onSalvar={salvarLinha} salvando={salvando} />
    </div>
  );
};

export default Movel;
